/**
 * Event detail — shows the wells on this event's plan with per-well
 * status icons. Tapping a well opens the wizard (Phase 3.20b).
 *
 * Status comes from merging three sources:
 *   - cached_field_records (server-side snapshot — synced rows)
 *   - local_field_records (PWA drafts not yet synced)
 *   - SamplingPlanItem (everything else is "planned")
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import type {
  AssignedEventEnvelope,
  FieldRecord,
  SamplingPlanItem,
} from '@argos/api-types';
import { ArrowLeftIcon, ChevronRight, ListChecks } from '../components/icons';
import clsx from 'clsx';
import { SkeletonCardList } from '../components/Skeleton';
import { EmptyState } from '../components/EmptyState';
import { SyncBadge } from '../components/SyncBadge';
import { STATUS_TONES } from '../styles/tokens';
import { getDb } from '../db';
import { useGpsFix } from '../hooks/useGpsFix';
import { useSiteGeofenceMonitor, type SitePolygon } from '../hooks/useSiteGeofence';
import { EventMap, type MapWell } from '../map/EventMap';
import { logEvent } from '../sync/log';

type WellStatus = 'planned' | 'draft' | 'complete' | 'flagged';

interface WellRow {
  well_id: string;
  well_name: string;
  status: WellStatus;
  /** Flag code set on the record, if any (e.g. "DR"). */
  flag_code: string | null;
  /** Most recent server-confirmed timestamp for sorting. */
  recency: number;
  lat: number | null;
  lon: number | null;
  /** PM's drag-reorder position from the SamplingPlanItem; lower = earlier. */
  sort_order: number | null;
}

export function EventDetailPage() {
  const { eventId = '' } = useParams();
  const navigate = useNavigate();
  const [envelope, setEnvelope] = useState<AssignedEventEnvelope | null>(null);
  const [wellRows, setWellRows] = useState<WellRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const db = getDb();
        const evRow = await db.cached_events.get(eventId);
        if (!evRow) {
          if (!cancelled) {
            setError('Event not found in cache');
            setWellRows([]);
          }
          return;
        }
        const env = evRow.data as AssignedEventEnvelope;
        if (cancelled) return;
        setEnvelope(env);

        const planItems = env.plan_items as SamplingPlanItem[];
        const serverRecs = env.existing_field_records ?? [];
        const localRecs = await db.local_field_records
          .where('event_id')
          .equals(eventId)
          .toArray();

        // Well coords from cached_wells (keyed by site_id).
        const wellRowsRaw = await db.cached_wells
          .where('site_id')
          .equals(env.event.site_id)
          .toArray();
        const coordsByWellId = new Map<string, { lat: number | null; lon: number | null }>();
        for (const wr of wellRowsRaw) {
          const d = wr.data as { id?: string; latitude?: number | null; longitude?: number | null };
          if (d.id) coordsByWellId.set(d.id, { lat: d.latitude ?? null, lon: d.longitude ?? null });
        }

        const recsByWell = new Map<string, FieldRecord>();
        for (const r of serverRecs) recsByWell.set(r.well_id, r);
        const localByWell = new Map<string, (typeof localRecs)[number]>();
        for (const l of localRecs) localByWell.set(l.well_id, l);

        // Deduplicate plan items by well — the new schema allows multiple
        // items per well (one per analyte group), but the field tech only
        // walks the well list. Keep the FIRST item per well (lowest sort_order
        // already, since planItems comes ordered) so the well-level
        // sort_order propagates.
        const seen = new Set<string>();
        const uniqByWell: Array<typeof planItems[number] & { sort_order?: number | null }> = [];
        for (const it of planItems) {
          if (seen.has(it.well_id)) continue;
          seen.add(it.well_id);
          uniqByWell.push(it as typeof planItems[number] & { sort_order?: number | null });
        }

        const rows: WellRow[] = uniqByWell.map((it) => {
          const server = recsByWell.get(it.well_id);
          const local = localByWell.get(it.well_id);
          let status: WellStatus = 'planned';
          if (server?.status === 'complete') status = 'complete';
          else if (local?.status === 'draft') status = 'draft';
          else if (server) status = 'draft';
          // 'flagged' comes from validation issues — wired in 3.20b
          // when the wizard surfaces them.
          const localData = local?.data as { flag_code?: string | null } | undefined;
          const flag_code =
            (server?.flag_code ?? localData?.flag_code ?? null) || null;
          const coords = coordsByWellId.get(it.well_id);
          return {
            well_id: it.well_id,
            well_name: it.well_name ?? it.well_id.slice(0, 8),
            status,
            flag_code,
            recency: local?.updated_at ?? 0,
            lat: coords?.lat ?? null,
            lon: coords?.lon ?? null,
            sort_order: it.sort_order ?? null,
          };
        });
        // Sort: planned/draft first (work-to-do), complete last. Within the
        // same status bucket, honor the PM's drag-reorder (sort_order); fall
        // back to alpha by well name when sort_order is missing.
        rows.sort((a, b) => {
          const order = (s: WellStatus) =>
            s === 'planned' ? 0 : s === 'draft' ? 1 : s === 'flagged' ? 2 : 3;
          const o = order(a.status) - order(b.status);
          if (o !== 0) return o;
          const ao = a.sort_order ?? Number.MAX_SAFE_INTEGER;
          const bo = b.sort_order ?? Number.MAX_SAFE_INTEGER;
          if (ao !== bo) return ao - bo;
          return a.well_name.localeCompare(b.well_name);
        });
        if (!cancelled) setWellRows(rows);
      } catch (err) {
        if (!cancelled) {
          setError((err as Error).message);
          setWellRows([]);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [eventId]);

  const completed = useMemo(
    () => wellRows?.filter((r) => r.status === 'complete').length ?? 0,
    [wellRows],
  );
  const total = wellRows?.length ?? 0;
  const allComplete = total > 0 && completed === total;

  // Site geofence monitor — fires once when the tech leaves the site
  // for ≥60s with unsampled wells.
  const [leavingSheetOpen, setLeavingSheetOpen] = useState(false);
  const [siteShape, setSiteShape] = useState<{
    centroid: { lat: number; lon: number } | null;
    polygon: SitePolygon | null;
    radius_m: number | null;
  }>({ centroid: null, polygon: null, radius_m: null });

  useEffect(() => {
    if (!envelope) return;
    let cancelled = false;
    void (async () => {
      const siteRow = await getDb().cached_sites.get(envelope.event.site_id);
      if (cancelled || !siteRow) return;
      const data = siteRow.data as {
        latitude?: number | null;
        longitude?: number | null;
        geofence_polygon?: SitePolygon | null;
        geofence_radius_m?: number | null;
      };
      setSiteShape({
        centroid:
          data.latitude != null && data.longitude != null
            ? { lat: data.latitude, lon: data.longitude }
            : null,
        polygon: data.geofence_polygon ?? null,
        radius_m: data.geofence_radius_m ?? null,
      });
    })();
    return () => {
      cancelled = true;
    };
  }, [envelope]);

  const onLeftWithUnsampled = useCallback(
    (info: { distance_m: number; pendingCount: number }) => {
      void logEvent('gps.site_left_with_unsampled', {
        event_id: eventId,
        ...info,
      });
      setLeavingSheetOpen(true);
    },
    [eventId],
  );

  const pendingCount = total - completed;
  useSiteGeofenceMonitor({
    enabled: !leavingSheetOpen && !allComplete && envelope != null,
    centroid: siteShape.centroid,
    polygon: siteShape.polygon,
    radius_m: siteShape.radius_m,
    pendingCount,
    onLeftWithUnsampled,
  });

  // Live GPS for the map's "you are here" marker.
  const gps = useGpsFix(envelope != null);
  const [showMap, setShowMap] = useState(false);
  const mapWells: MapWell[] = useMemo(
    () =>
      (wellRows ?? [])
        .filter((w) => w.lat != null && w.lon != null)
        .map((w) => ({
          well_id: w.well_id,
          well_name: w.well_name,
          lat: w.lat as number,
          lon: w.lon as number,
          status: w.status,
        })),
    [wellRows],
  );

  if (error) {
    return (
      <div className="flex-1 flex flex-col items-center justify-center p-6">
        <p className="text-accent-red mb-4">{error}</p>
        <Link to="/events" className="btn-secondary">
          Back to events
        </Link>
      </div>
    );
  }

  return (
    <div className="flex-1 flex flex-col p-6">
      <Link
        to="/events"
        className="inline-flex items-center gap-1 text-mono-400 text-sm mb-4"
      >
        <ArrowLeftIcon className="h-4 w-4" /> All events
      </Link>

      {envelope && (
        <>
          <h1 className="text-2xl font-semibold">{envelope.event.name}</h1>
          <p className="text-mono-400 text-sm mb-1">{envelope.event.site_name}</p>
          <p className="text-mono-500 text-xs mb-4">
            {completed} of {total} wells complete
          </p>
        </>
      )}

      {/* Map toggle — shown when at least one well has coords. */}
      {mapWells.length > 0 && (
        <div className="mb-4">
          {showMap ? (
            <>
              <EventMap
                polygon={siteShape.polygon}
                centroid={siteShape.centroid}
                wells={mapWells}
                position={
                  gps.fix
                    ? { lat: gps.fix.lat, lon: gps.fix.lon, accuracy_m: gps.fix.accuracy_m }
                    : null
                }
                onWellTap={(wid) =>
                  navigate(`/events/${eventId}/wells/${wid}/wizard`)
                }
              />
              <button
                type="button"
                className="text-mono-500 text-xs mt-1"
                onClick={() => setShowMap(false)}
              >
                Hide map
              </button>
            </>
          ) : (
            <button
              type="button"
              className="btn-secondary w-full text-sm"
              onClick={() => setShowMap(true)}
            >
              Show map ({mapWells.length} well{mapWells.length === 1 ? '' : 's'})
            </button>
          )}
        </div>
      )}

      {wellRows == null ? (
        <SkeletonCardList count={4} />
      ) : wellRows.length === 0 ? (
        <EmptyState
          icon={ListChecks}
          title="No plan items on this event"
          hint="The event was scheduled without any wells. Use Quick reading to capture an ad-hoc reading."
        />
      ) : (
        <ul className="space-y-2">
          {wellRows.map((w) => {
            const tone = STATUS_TONES[w.status === 'complete' ? 'complete' : w.status === 'draft' ? 'in_progress' : w.status === 'flagged' ? 'flagged' : 'planned'];
            return (
            <li key={w.well_id}>
              <button
                type="button"
                disabled={w.status === 'complete'}
                onClick={() =>
                  navigate(`/events/${eventId}/wells/${w.well_id}/wizard`)
                }
                className={`w-full text-left flex items-center gap-3 p-3 rounded-xl border transition-colors min-h-[56px] ${
                  w.status === 'complete'
                    ? 'border-mono-800 bg-mono-900/30 opacity-80'
                    : 'border-mono-800 bg-mono-900/40 hover:bg-mono-900/60 active:bg-mono-900 hover:border-mono-700'
                }`}
              >
                {/* Left-edge accent bar — more obvious on a phone than a tiny dot. */}
                <div className={clsx('h-10 w-1 rounded-full shrink-0', tone.bar)} aria-hidden />
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-mono-100 flex items-center gap-2 truncate">
                    {w.well_name}
                    {w.flag_code && (
                      <span className="text-[10px] font-semibold uppercase tracking-wide text-accent-amber bg-accent-amber/15 border border-accent-amber/40 rounded px-1.5 py-0.5">
                        {w.flag_code}
                      </span>
                    )}
                  </div>
                  <div className="text-mono-500 text-xs">
                    {labelFor(w.status)}
                  </div>
                </div>
                {w.status === 'complete'
                  ? <SyncBadge state="synced" />
                  : w.status === 'draft' ? <SyncBadge state="draft" />
                  : <ChevronRight className="h-5 w-5 text-mono-500" strokeWidth={2} />}
              </button>
            </li>
            );
          })}
        </ul>
      )}

      <div className="mt-6 space-y-2">
        <button
          type="button"
          className="btn-primary w-full"
          disabled={!allComplete}
          onClick={() =>
            navigate(`/events/${eventId}/close`, {
              state: { unsampled: 0 },
            })
          }
        >
          {allComplete ? 'Close event' : `${total - completed} wells remaining`}
        </button>
        {!allComplete && (
          <button
            type="button"
            className="btn-secondary w-full text-sm"
            onClick={() =>
              navigate(`/events/${eventId}/close`, {
                state: { unsampled: total - completed },
              })
            }
          >
            Close event with reason…
          </button>
        )}
      </div>

      {leavingSheetOpen && (
        <LeavingSiteSheet
          eventId={eventId}
          unsampled={pendingCount}
          onClose={() => setLeavingSheetOpen(false)}
        />
      )}
    </div>
  );
}

function LeavingSiteSheet({
  eventId,
  unsampled,
  onClose,
}: {
  eventId: string;
  unsampled: number;
  onClose: () => void;
}) {
  const navigate = useNavigate();
  return (
    <div className="fixed inset-0 z-40 bg-black/60 flex items-end justify-center">
      <div className="w-full max-w-md bg-mono-900 border-t border-mono-800 rounded-t-lg p-5 space-y-3">
        <h2 className="text-lg font-semibold">You're leaving the site</h2>
        <p className="text-mono-300 text-sm">
          {unsampled} well{unsampled === 1 ? '' : 's'} on the plan{' '}
          {unsampled === 1 ? 'has' : 'have'} not been sampled yet.
        </p>
        <div className="space-y-2 pt-2">
          <button
            type="button"
            className="btn-primary w-full"
            onClick={onClose}
          >
            Return to the site
          </button>
          <button
            type="button"
            className="btn-secondary w-full"
            onClick={() => {
              onClose();
              navigate('/events', { replace: false });
            }}
          >
            Save & continue later
          </button>
          <button
            type="button"
            className="btn-secondary w-full text-accent-amber"
            onClick={() => {
              onClose();
              navigate(`/events/${eventId}/close`, {
                state: { unsampled },
              });
            }}
          >
            Close event with reason
          </button>
        </div>
      </div>
    </div>
  );
}

function labelFor(s: WellStatus): string {
  switch (s) {
    case 'planned':
      return 'Not yet sampled';
    case 'draft':
      return 'Draft — not yet synced';
    case 'complete':
      return 'Complete';
    case 'flagged':
      return 'Validation warnings';
  }
}

// StatusIcon removed — the left-edge accent bar + SyncBadge in the row
// header replace it for richer signal.
